import {
    buildIncomeCollectionConfirmedMessage,
    type IncomeCollectionType,
} from '@fruitful/llm-service-shared';
import type { IncomeCollectionWidgetProps } from '@fruitful/types';
import React, { useCallback, useState } from 'react';
import {
    ArrowUpIcon,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H6Regular,
    Icon,
    IconButton,
    Input,
    LabelEmphasized,
    PencilIcon,
    SlideRulePicker,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { CHAT_INPUT_WIDGET } from '../shared-chat/ChatLayout';
import { BaseInputWidget } from './BaseInputWidget';

interface IncomeCollectionWidgetComponentProps extends IncomeCollectionWidgetProps {
    disabled?: boolean;
    isActive?: boolean;
    onSubmit: (
        displayText: string,
        agentPayload: string,
    ) => void | Promise<void>;
}

type Step = 'type-select' | 'slider';

interface IncomeOption {
    id: IncomeCollectionType;
    label: string;
}

const OPTIONS: IncomeOption[] = [
    { id: 'monthly-take-home', label: 'Monthly take-home pay' },
    { id: 'yearly-salary', label: 'Yearly salary' },
];

const MONTHLY_MIN = 0;
const MONTHLY_MAX = 30000;
const MONTHLY_STEP = 100;

const YEARLY_MIN = 0;
const YEARLY_MAX = 500000;
const YEARLY_STEP = 1000;

const COMPOSER_LEADING_SLOT_SIZE = 40;

const formatCurrency = (value: number): string =>
    `$${Math.round(value).toLocaleString()}`;

const clamp = (value: number, min: number, max: number): number =>
    Math.min(Math.max(value, min), max);

export const IncomeCollectionWidget = ({
    isJoint,
    disabled = false,
    isActive = true,
    onSubmit,
}: IncomeCollectionWidgetComponentProps) => {
    const [step, setStep] = useState<Step>('type-select');
    const [selectedType, setSelectedType] =
        useState<IncomeCollectionType | null>(null);
    const [sliderValue, setSliderValue] = useState(0);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [customInputValue, setCustomInputValue] = useState('');
    const [isManualMode, setIsManualMode] = useState(false);
    const [manualText, setManualText] = useState('');

    const isWidgetDisabled = disabled || !isActive || hasSubmitted;

    const isMonthly = selectedType === 'monthly-take-home';
    const min = isMonthly ? MONTHLY_MIN : YEARLY_MIN;
    const max = isMonthly ? MONTHLY_MAX : YEARLY_MAX;
    const stepSize = isMonthly ? MONTHLY_STEP : YEARLY_STEP;

    const submitResult = useCallback(
        (
            incomeType: IncomeCollectionType,
            value: number | null,
            text: string | null,
        ) => {
            if (isWidgetDisabled) return;
            setHasSubmitted(true);

            let displayText: string;
            if (incomeType === 'custom') {
                displayText = text ?? '';
            } else if (incomeType === 'monthly-take-home') {
                displayText = `${formatCurrency(value ?? 0)}/month take-home`;
            } else {
                displayText = `${formatCurrency(value ?? 0)}/year salary`;
            }

            const agentPayload = buildIncomeCollectionConfirmedMessage({
                displayText,
                payload: { incomeType, value, text },
            });

            void onSubmit(displayText, agentPayload);
        },
        [isWidgetDisabled, onSubmit],
    );

    const handleTypeSelect = useCallback(
        (type: IncomeCollectionType) => {
            if (isWidgetDisabled) return;
            setSelectedType(type);
            setSliderValue(0);
            setStep('slider');
        },
        [isWidgetDisabled],
    );

    const handleBack = useCallback(() => {
        if (isWidgetDisabled) return;
        setStep('type-select');
        setSelectedType(null);
        setSliderValue(0);
        setIsManualMode(false);
    }, [isWidgetDisabled]);

    const handleCustomSend = useCallback(() => {
        const trimmed = customInputValue.trim();
        if (isWidgetDisabled || trimmed === '') return;
        submitResult('custom', null, trimmed);
    }, [customInputValue, isWidgetDisabled, submitResult]);

    const handleSliderSubmit = useCallback(() => {
        if (isWidgetDisabled || selectedType == null) return;
        submitResult(selectedType, sliderValue, null);
    }, [isWidgetDisabled, selectedType, sliderValue, submitResult]);

    const openManualEntry = useCallback(() => {
        if (isWidgetDisabled) return;
        setManualText(String(Math.round(sliderValue)));
        setIsManualMode(true);
    }, [isWidgetDisabled, sliderValue]);

    const cancelManualEntry = useCallback(() => {
        setIsManualMode(false);
    }, []);

    const submitManualEntry = useCallback(() => {
        if (isWidgetDisabled || selectedType == null) return;
        const cleaned = manualText.replace(/[^0-9.]/g, '');
        const parsed = cleaned === '' ? NaN : Number(cleaned);
        const next = Number.isNaN(parsed)
            ? sliderValue
            : clamp(parsed, min, max);
        setSliderValue(next);
        setIsManualMode(false);
        submitResult(selectedType, next, null);
    }, [
        isWidgetDisabled,
        selectedType,
        manualText,
        sliderValue,
        min,
        max,
        submitResult,
    ]);

    const canSend = !isWidgetDisabled && customInputValue.trim() !== '';

    const sliderPromptText = `What is your ${isJoint ? 'combined' : ''} ${selectedType === 'monthly-take-home' ? 'monthly take-home pay' : 'yearly salary'}?`;

    if (step === 'type-select') {
        return (
            <BaseInputWidget
                alignSelf="stretch"
                width="100%"
                paddingTop={CHAT_INPUT_WIDGET.paddingTop}
                paddingBottom={0}
            >
                <YStack
                    paddingHorizontal={CHAT_INPUT_WIDGET.rowPaddingHorizontal}
                    paddingBottom={12}
                    gap={4}
                >
                    <LabelEmphasized>(1 OF 2)</LabelEmphasized>
                    <H6Regular>
                        How would you like to provide your income number?
                    </H6Regular>
                </YStack>
                {OPTIONS.map((option, i) => (
                    <XStack
                        gap={12}
                        key={option.id}
                        alignItems="center"
                        paddingHorizontal={
                            CHAT_INPUT_WIDGET.rowPaddingHorizontal
                        }
                        paddingVertical={CHAT_INPUT_WIDGET.rowPaddingVertical}
                        cursor={isWidgetDisabled ? 'default' : 'pointer'}
                        pointerEvents={isWidgetDisabled ? 'none' : 'auto'}
                        onPress={() => {
                            handleTypeSelect(option.id);
                        }}
                        pressStyle={
                            !isWidgetDisabled ? { opacity: 0.7 } : undefined
                        }
                    >
                        <View
                            width={40}
                            height={40}
                            justifyContent="center"
                            alignItems="center"
                            backgroundColor="$gray200"
                            borderRadius={16}
                        >
                            <BodyMediumRegular>{i + 1}</BodyMediumRegular>
                        </View>
                        <YStack
                            flex={1}
                            minHeight={CHAT_INPUT_WIDGET.rowMinHeight}
                            justifyContent="center"
                            pointerEvents="none"
                        >
                            <BodyMediumRegular>
                                {option.label}
                            </BodyMediumRegular>
                        </YStack>
                    </XStack>
                ))}
                <XStack
                    alignItems="center"
                    gap={CHAT_INPUT_WIDGET.rowGap}
                    minHeight={48}
                    paddingLeft={CHAT_INPUT_WIDGET.rowPaddingHorizontal}
                    paddingRight={8}
                    paddingTop={CHAT_INPUT_WIDGET.rowPaddingVertical}
                    paddingBottom={8}
                >
                    <XStack
                        width={COMPOSER_LEADING_SLOT_SIZE}
                        height={COMPOSER_LEADING_SLOT_SIZE}
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                    >
                        <Icon icon={PencilIcon} size={16} color="$gray1000" />
                    </XStack>
                    <XStack
                        flex={1}
                        minHeight={CHAT_INPUT_WIDGET.rowMinHeight}
                        alignItems="center"
                    >
                        <Input
                            flex={1}
                            width="100%"
                            size="regular"
                            fontWeight="regular"
                            placeholder="Tell us in your own words..."
                            placeholderTextColor="$gray1000"
                            value={customInputValue}
                            disabled={isWidgetDisabled}
                            height={40}
                            padding={0}
                            paddingLeft={0}
                            paddingRight={8}
                            paddingVertical={0}
                            borderWidth={0}
                            backgroundColor="transparent"
                            focusStyle={{
                                borderWidth: 0,
                                outlineWidth: 0,
                            }}
                            hoverStyle={{
                                borderWidth: 0,
                                outlineWidth: 0,
                            }}
                            containerProps={{
                                flex: 1,
                                width: '100%',
                                height: 40,
                                alignItems: 'center',
                                backgroundColor: 'transparent',
                            }}
                            onChangeText={setCustomInputValue}
                            onSubmitEditing={handleCustomSend}
                        />
                    </XStack>
                    <IconButton
                        variant="primary"
                        icon={ArrowUpIcon}
                        disabled={!canSend}
                        onPress={handleCustomSend}
                    />
                </XStack>
            </BaseInputWidget>
        );
    }

    return (
        <BaseInputWidget
            alignSelf="stretch"
            width="100%"
            maxWidth="100%"
            paddingTop={CHAT_INPUT_WIDGET.paddingTop}
            paddingBottom={12}
        >
            <YStack
                gap={12}
                paddingHorizontal={CHAT_INPUT_WIDGET.rowPaddingHorizontal}
            >
                <YStack gap={4}>
                    <LabelEmphasized>(2 OF 2)</LabelEmphasized>
                    <H6Regular paddingBottom={4}>{sliderPromptText}</H6Regular>
                </YStack>

                {isManualMode ? (
                    <YStack gap={12}>
                        <Input
                            value={manualText}
                            onChangeText={setManualText}
                            keyboardType="numeric"
                            placeholder="Enter an exact amount"
                            onSubmitEditing={submitManualEntry}
                            returnKeyType="done"
                        />
                        <XStack gap={8}>
                            <XStack flex={1}>
                                <Button
                                    width="full"
                                    text="Cancel"
                                    variant={ButtonVariant.SECONDARY}
                                    size={ButtonSize.LARGE}
                                    onPress={cancelManualEntry}
                                />
                            </XStack>
                            <XStack flex={1}>
                                <Button
                                    width="full"
                                    text="Set"
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.LARGE}
                                    disabled={isWidgetDisabled}
                                    onPress={submitManualEntry}
                                />
                            </XStack>
                        </XStack>
                    </YStack>
                ) : (
                    <YStack
                        marginHorizontal={
                            -CHAT_INPUT_WIDGET.rowPaddingHorizontal
                        }
                        gap={24}
                    >
                        <SlideRulePicker
                            min={min}
                            max={max}
                            step={stepSize}
                            initialValue={sliderValue}
                            verticalGap={10}
                            shortStepColor="$gray900"
                            longStepColor="$gray900"
                            textFormatter={(raw: string) =>
                                formatCurrency(Number(raw))
                            }
                            onValueChangeEnd={setSliderValue}
                            onEditPress={openManualEntry}
                        />
                        <XStack
                            gap={8}
                            marginHorizontal={
                                CHAT_INPUT_WIDGET.rowPaddingHorizontal
                            }
                        >
                            <XStack flex={1}>
                                <Button
                                    width="full"
                                    text="Back"
                                    variant={ButtonVariant.SECONDARY}
                                    size={ButtonSize.LARGE}
                                    disabled={isWidgetDisabled}
                                    onPress={handleBack}
                                />
                            </XStack>
                            <XStack flex={1}>
                                <Button
                                    width="full"
                                    text="Next"
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.LARGE}
                                    disabled={isWidgetDisabled}
                                    onPress={handleSliderSubmit}
                                />
                            </XStack>
                        </XStack>
                    </YStack>
                )}
            </YStack>
        </BaseInputWidget>
    );
};
